import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Recipient } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { UseInfiniteQueryResult } from 'react-query';
import { METL_LIST_RECIPIENTS_PAGE_SIZE, useMetlListRecipients } from '@frontend/api-bulk-messaging';
import { useAudienceTray } from '../providers/audience-tray-provider';
import { convertRecipients } from '../utils/convert-recipients';

export const useRecipientsView = (searchQuery?: string) => {
  const { campaignId, filters, selectedRecipients, setSelectedRecipients } = useAudienceTray();
  const metlListRecipientsResponse = useMetlListRecipients(filters, METL_LIST_RECIPIENTS_PAGE_SIZE, searchQuery);
  const { data, hasNextPage, fetchNextPage, isFetching } = metlListRecipientsResponse;
  const [selectingAllRecipients, setSelectingAllRecipients] = useState(false);
  const abortControllerRef = useRef<AbortController>();

  const startSelectAllRecipients = useCallback(async () => {
    if (selectingAllRecipients || abortControllerRef.current) return;
    setSelectingAllRecipients(true);
    abortControllerRef.current = new AbortController();
  }, [abortControllerRef, selectingAllRecipients]);

  const endSelectAllRecipients = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = undefined;
    }
    setSelectingAllRecipients(false);
  }, []);

  useEffect(() => {
    if (!selectingAllRecipients) return;

    if (hasNextPage && !isFetching) {
      fetchNextPage();
      return;
    }

    if (!hasNextPage && !isFetching) {
      setSelectedRecipients((selectedRecipients) => {
        const metlRecipients = data?.pages.map((page) => page.rows ?? []).flat() ?? [];
        const allRecipients = convertRecipients(metlRecipients, campaignId);
        const unselectedRecipients = getUnselectedRecipients(allRecipients, selectedRecipients);
        return [...selectedRecipients, ...unselectedRecipients];
      });
      endSelectAllRecipients();
    }
  }, [
    campaignId,
    data?.pages,
    endSelectAllRecipients,
    fetchNextPage,
    hasNextPage,
    isFetching,
    selectingAllRecipients,
    setSelectedRecipients,
  ]);

  const infiniteQueryProps = useMemo(() => {
    const pages = data?.pages ?? [];
    const metlRecipients = pages.map((page) => page.rows ?? []).flat();
    const allRecipients = convertRecipients(metlRecipients, campaignId);
    const displayedRecipients = getUnselectedRecipients(allRecipients, selectedRecipients);

    return {
      ...metlListRecipientsResponse,
      data: { ...data, pages: [{ rows: displayedRecipients }] },
    } as UseInfiniteQueryResult<{ rows: Recipient[] | undefined }, unknown>;
  }, [metlListRecipientsResponse, selectedRecipients, data, campaignId]);

  const deselectAllRecipients = useCallback(() => {
    setSelectedRecipients([]);
  }, [setSelectedRecipients]);

  const selectRecipient = useCallback(
    (recipient: Recipient) => {
      setSelectedRecipients((selectedRecipients) => [...selectedRecipients, recipient]);
    },
    [setSelectedRecipients]
  );

  const deselectRecipient = useCallback(
    (recipient: Recipient) => {
      setSelectedRecipients((selectedRecipients) =>
        selectedRecipients.filter((selectedRecipient) => selectedRecipient.destination !== recipient.destination)
      );
    },
    [setSelectedRecipients]
  );

  return {
    selectRecipient,
    deselectRecipient,
    deselectAllRecipients,
    selectAllRecipients: startSelectAllRecipients,
    abortSelectAllRecipients: endSelectAllRecipients,
    isSelectAllLoading: selectingAllRecipients,
    infiniteQueryProps,
  };
};

const getUnselectedRecipients = (allRecipients: Recipient[], selectedRecipients: Recipient[]) =>
  allRecipients.filter(
    (recipient) => !selectedRecipients.find((selected) => selected.destination === recipient.destination)
  );
